import { GDFHotPollyTypes } from '../../../gdf/type';
import { ProbSeverePrimaryDisplay, ProbSevereSecondaryDisplay, RadarSummaryArrowStyle } from '../../../settings/settings';
import { StyleFactory, WeatherLayerSpecification } from '../styling';

export const tcfLayers: StyleFactory = (baseId) => {
    return [
        {
            type: 'line',
            id: baseId + '-line',
            source: baseId,
            paint: {
                'line-color': 'rgba(107,162,206,1)',
                'line-width': 2
            }
        },
        {
            type: 'fill',
            id: baseId + '-fill-solid',
            pickable: true,
            filter: ['==', ['get', 'c'], 1],
            source: baseId,
            paint: {
                // prettier-ignore
                'fill-color': [
                    'case',
                    ['==', ['get', 'p'], 1], 'rgba(107,162,206,1)',
                    'rgba(128,128,128,1)'
                ]
            }
        },
        {
            type: 'fill',
            pickable: true,
            filter: ['==', ['get', 'c'], 2],
            id: baseId + '-fill-pattern-2',
            source: baseId,
            paint: {
                // prettier-ignore
                'fill-pattern': [
                    'case',
                    ['==', ['get', 'p'], 1], ['image', 'tcf-c2-p1'],
                    ['image', 'tcf-c2-p2']
                ]
            }
        },
        {
            type: 'fill',
            pickable: true,
            filter: ['==', ['get', 'c'], 3],
            id: baseId + '-fill-pattern-3',
            source: baseId,
            paint: {
                // prettier-ignore
                'fill-pattern': [
                    'case',
                    ['==', ['get', 'p'], 1], ['image', 'tcf-c3-p1'],
                    ['image', 'tcf-c3-p2']
                ]
            }
        },
        {
            type: 'fill',
            id: baseId + '-hover',
            source: baseId,
            paint: {
                // prettier-ignore
                'fill-color': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false], 'rgba(0,0,0,0.22)',
                    'rgba(0,0,0,0)'
                ]
            }
        }
    ];
};

export const severeWeatherProbLayers: StyleFactory = (baseId, settings, prodId, isLight) => {
    let primaryProp = '';
    switch (settings.winds.probSeverePrimaryDisplay) {
        case ProbSeverePrimaryDisplay.combined:
            primaryProp = 'probSevere';
            break;
        case ProbSeverePrimaryDisplay.hail:
            primaryProp = 'probHail';
            break;
        case ProbSeverePrimaryDisplay.tornado:
            primaryProp = 'probTornado';
            break;
        case ProbSeverePrimaryDisplay.wind:
            primaryProp = 'probWind';
            break;
    }

    let secondaryProp = '';
    switch (settings.winds.probSevereSecondaryDisplay) {
        case ProbSevereSecondaryDisplay.none:
            secondaryProp = '';
            break;
        case ProbSevereSecondaryDisplay.hail:
            secondaryProp = 'probHail';
            break;
        case ProbSevereSecondaryDisplay.tornado:
            secondaryProp = 'probTornado';
            break;
        case ProbSevereSecondaryDisplay.wind:
            secondaryProp = 'probWind';
            break;
    }

    const layers: WeatherLayerSpecification[] = [
        {
            id: baseId + '-prob-severe-primary',
            source: baseId,
            type: 'line',
            filter: [
                'all',
                ['==', ['get', 'type'], GDFHotPollyTypes.probsevere],
                ['>=', ['get', primaryProp], settings.winds.probSeverePrimaryMinLevel]
            ],
            paint: {
                'line-width': 2,
                // prettier-ignore
                'line-color': [
                    'case',
                    ['>=', ['get', primaryProp], 90], 'rgb(255, 0, 255)',
                    ['>=', ['get', primaryProp], 70], 'rgb(255, 0, 0)',
                    ['>=', ['get', primaryProp], 50], 'rgb(255, 255, 0)',
                    ['>=', ['get', primaryProp], 30], 'rgb(0, 0, 255)',
                    ['>=', ['get', primaryProp], 0], ['global-state', 'text-color'],
                    'transparent'
                ]
            }
        },
        {
            id: baseId + '-prob-severe-fill',
            source: baseId,
            pickable: true,
            type: 'fill',
            filter: ['==', ['get', 'type'], GDFHotPollyTypes.probsevere],
            paint: {
                'fill-color': 'transparent'
            }
        }
    ];

    if (secondaryProp) {
        layers.push({
            id: baseId + '-prob-severe-secondary',
            source: baseId,
            type: 'line',
            filter: [
                'all',
                ['==', ['get', 'type'], GDFHotPollyTypes.probsevere],
                ['>=', ['get', primaryProp], settings.winds.probSeverePrimaryMinLevel],
                ['>=', ['get', secondaryProp], settings.winds.probSevereSecondaryMinLevel]
            ],
            paint: {
                'line-offset': -4,
                'line-width': 2,
                // prettier-ignore
                'line-color': [
                    'case',
                    ['>=', ['get', secondaryProp], 90], 'rgb(255, 0, 255)',
                    ['>=', ['get', secondaryProp], 70], 'rgb(255, 0, 0)',
                    ['>=', ['get', secondaryProp], 50], 'rgb(255, 255, 0)',
                    ['>=', ['get', secondaryProp], 30], 'rgb(0, 0, 255)',
                    ['>=', ['get', secondaryProp], 0], ['global-state', 'text-color'],
                    'transparent'
                ]
            }
        });
    }

    if (settings.winds.probSevereShowTrail) {
        layers.push({
            id: baseId + '-storm-track',
            source: baseId,
            type: 'line',
            filter: [
                'all',
                ['==', ['get', 'type'], GDFHotPollyTypes.storm_track],
                ['>=', ['get', primaryProp], settings.winds.probSeverePrimaryMinLevel]
            ],
            paint: {
                'line-dasharray': [2, 1],
                'line-width': 2,
                'line-color': ['global-state', 'text-color']
            }
        });
    }

    if (settings.winds.probSevereShowArrow) {
        const speedBased = settings.radar.radarSummarySpeedArrowStyle === RadarSummaryArrowStyle.speedBased;
        if (speedBased) {
            layers.push(
                {
                    id: baseId + '-circles',
                    type: 'circle',
                    filter: ['==', ['get', 'type'], 's'],
                    source: baseId,
                    paint: {
                        'circle-color': ['global-state', 'text-color'],
                        'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 4, 3, 6, 4]
                    }
                },
                {
                    id: baseId + '-tops',
                    type: 'symbol',
                    filter: ['all', ['==', ['get', 'type'], 's'], ['!=', ['get', 'tops'], ['literal', null]]],
                    source: baseId,
                    layout: {
                        'text-field': ['get', 'tops'],
                        'text-anchor': 'top',
                        'text-offset': [0, 0.5],
                        'text-font': ['Open Sans Regular'],
                        'text-size': 10
                    },
                    paint: {
                        'text-color': ['global-state', 'text-color']
                    }
                },
                {
                    id: baseId + '-lines',
                    type: 'line',
                    filter: ['==', ['get', 'type'], 'l'],
                    source: baseId,
                    paint: {
                        'line-color': ['global-state', 'text-color'],
                        'line-width': ['interpolate', ['linear'], ['zoom'], 0, 1, 4, 2]
                    }
                },
                {
                    id: baseId + '-arrows',
                    filter: ['==', ['get', 'type'], 'e'],
                    type: 'symbol',
                    source: baseId,
                    layout: {
                        'icon-image': ['image', 'fe-weather:arrow'],
                        'icon-rotate': ['get', 'dir'],
                        'icon-rotation-alignment': 'map',
                        'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0.1, 4, 0.25, 6, 0.6],
                        'icon-allow-overlap': true,
                        'symbol-placement': 'point'
                    },
                    paint: {
                        'icon-color': ['global-state', 'text-color']
                    }
                },
                {
                    minzoom: 4,
                    id: baseId + '-midpoints',
                    type: 'symbol',
                    filter: ['==', ['get', 'type'], 'm'],
                    source: baseId,
                    layout: {
                        'icon-image': ['image', 'fe-weather:line'],
                        'icon-allow-overlap': true,
                        'icon-anchor': 'center',
                        'icon-rotate': ['get', 'dir'],
                        'icon-rotation-alignment': 'map',
                        'icon-size': ['interpolate', ['linear'], ['zoom'], 0, 0, 8, 0.7]
                    },
                    paint: {
                        'icon-color': ['global-state', 'text-color']
                    }
                },
                {
                    id: baseId + '-speed',
                    minzoom: 4,
                    filter: ['==', ['get', 'type'], 'e'],
                    source: baseId,
                    type: 'symbol',
                    layout: {
                        'text-field': ['get', 'speed'],
                        'text-offset': [0, -0.5],
                        'text-allow-overlap': true,
                        'text-anchor': 'bottom',
                        'text-font': ['Open Sans Regular'],
                        'text-size': 10
                    },
                    paint: {
                        'text-color': ['global-state', 'text-color']
                    }
                }
            );
        } else {
            layers.push(
                {
                    id: baseId + '-arrows',
                    source: baseId,
                    filter: ['==', ['get', 'type'], 's'],
                    type: 'symbol',
                    layout: {
                        'icon-image': ['image', 'fe-weather:radar-summary-arrow'],
                        'icon-rotate': ['get', 'dir'],
                        'icon-anchor': 'bottom',
                        'icon-offset': [0, 7],
                        'icon-allow-overlap': true,
                        'icon-rotation-alignment': 'map',
                        'icon-size': 1
                    },
                    paint: {
                        'icon-color': ['global-state', 'text-color']
                    }
                },
                {
                    id: baseId + '-speed',
                    minzoom: 4,
                    filter: ['==', ['get', 'type'], 's'],
                    source: baseId,
                    type: 'symbol',
                    layout: {
                        'text-field': ['get', 'speed'],
                        'text-allow-overlap': true,
                        'text-anchor': 'center',
                        'text-font': ['Open Sans Regular'],
                        'text-size': 10,
                        'text-offset': ['get', 'offset'],
                        'text-rotation-alignment': 'map'
                    },
                    paint: {
                        'text-color': ['global-state', 'text-color']
                    }
                }
            );
        }
    }

    return layers;
};
